import React, { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import PredefinedDateRanges from "../../../../core/common/datePicker";
import CommonSelect from "../../../../core/common/commonSelect";
import { TreeSelect } from "antd";

interface ProductFilterProps {
  data: any[];
  setFilteredData: React.Dispatch<React.SetStateAction<any[]>>;
  assetCategoryOptions: any[];
  searchQuery?: any;
  setSearchQuery?: any;
}

interface CategoryNode {
  id: string;
  name: string;
  status: string;
  category_level: number;
  parentId?: string | null;
  subcategories?: CategoryNode[];
}

const ProductGridFilter: React.FC<ProductFilterProps> = ({
  data,
  setFilteredData,
  assetCategoryOptions,
  searchQuery,
  setSearchQuery,
}) => {
  // State: Filters
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [warrantyFilter, setWarrantyFilter] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] =
    useState<moment.Moment | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(
    null
  );
  const [categoryFilter, setCategoryFilter] = useState<string | any>(undefined);

  const activeFiltersCount = [
    statusFilter,
    selectedStartDate,
    selectedEndDate,
    warrantyFilter,
    searchQuery,
    categoryFilter,
  ].filter(Boolean).length;

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  };

  const handleClearFilters = () => {
    setStatusFilter(null);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setWarrantyFilter(null);
    setSearchQuery("");
    setCategoryFilter(undefined);
  };

  useEffect(() => {
    let newFilteredData = [...data];
    if (statusFilter) {
      newFilteredData = newFilteredData.filter(
        (item) => item.status === statusFilter
      );
    }
    if (categoryFilter && categoryFilter !== "") {
      newFilteredData = newFilteredData.filter((item) => {
        return item?.category?.path?.some(
          (pathItem: any) =>
            pathItem?.id?.toString() === categoryFilter?.toString()
        );
      });
    }
    if (selectedStartDate && selectedEndDate) {
      newFilteredData = newFilteredData.filter((item) => {
        const createdAt = new Date(item.created_at);
        return (
          createdAt >= selectedStartDate.toDate() &&
          createdAt <= selectedEndDate.toDate()
        );
      });
    }
    if (warrantyFilter) {
      if (warrantyFilter === "Expired") {
        newFilteredData = newFilteredData.filter((item) => {
          const warranty = new Date(item.warranty);
          return warranty < new Date();
        });
      } else if (warrantyFilter === "Not Expired") {
        newFilteredData = newFilteredData.filter((item) => {
          const warranty = new Date(item.warranty);
          return warranty >= new Date();
        });
      }
    }
    if (searchQuery?.trim()) {
      const query = searchQuery.toLowerCase().trim();
      newFilteredData = newFilteredData.filter(
        (item) =>
          item?.brand?.toLowerCase().includes(query) ||
          item?.model?.toLowerCase().includes(query) ||
          item?.name?.toLowerCase().includes(query) ||
          item?.serial_no?.toLowerCase().includes(query) ||
          item?.status?.toLowerCase().includes(query)
      );
    }

    setFilteredData(newFilteredData);
  }, [
    statusFilter,
    selectedStartDate,
    selectedEndDate,
    data,
    warrantyFilter,
    searchQuery,
    categoryFilter,
  ]);

  // Category Updated
  const handleCategorySelectionChange = (categoryValue: string) => {
    setCategoryFilter(categoryValue);
  };

  const categoryTreeData = useMemo(() => {
    const convertCategoriesToTreeFormat = (
      categories: CategoryNode[]
    ): any[] => {
      if (!categories || !Array.isArray(categories)) {
        return [];
      }

      return categories
        .filter((category) => category && category.status === "Active")
        .map((category) => {
          const hasChildCategories =
            category.subcategories && category.subcategories.length > 0;

          return {
            title: category.name,
            value: category.id,
            key: category.id,
            children: hasChildCategories
              ? convertCategoriesToTreeFormat(category.subcategories!)
              : undefined,
          };
        });
    };

    return convertCategoriesToTreeFormat(assetCategoryOptions || []);
  }, [assetCategoryOptions]);

  return (
    <>
      <div className="d-flex my-xl-auto justify-content-end align-items-center flex-wrap row-gap-3  w-100">
        <div className="input-group me-2" style={{ maxWidth: "200px" }}>
          <span className="input-group-text">
            <i className="ti ti-search" />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setSearchQuery("")}>
              <i className="ti ti-x" />
            </button>
          )}
        </div>
        {/* Category Filter */}
        <div
          className="ant-tree-select-div me-2"
          style={{ width: "250px" }}
          title="Category Filter">
          <TreeSelect
            value={categoryFilter}
            showSearch
            treeNodeFilterProp="title"
            filterTreeNode={(input, node) => {
              if (!input || !node) return false;
              const inputLower = input.toLowerCase();
              const title = node.title?.toString().toLowerCase() || "";
              return title.includes(inputLower);
            }}
            searchValue={undefined}
            autoClearSearchValue={true}
            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
            treeData={categoryTreeData}
            placeholder="Please select a category"
            allowClear
            treeDefaultExpandAll={false}
            onChange={handleCategorySelectionChange}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
          />
        </div>

        {/* Status Filter */}
        <div
          className="me-2 fs-13"
          style={{ minWidth: "130px" }}
          title="Status Filter">
          <CommonSelect
            options={[
              { value: "", label: "Select Status" },
              { value: "Active", label: "Active" },
              { value: "Inactive", label: "Inactive" },
            ]}
            defaultValue={
              statusFilter
                ? { value: statusFilter, label: statusFilter }
                : { value: "", label: "Select Status" }
            }
            onChange={(selected: any) => setStatusFilter(selected?.value || "")}
            placeHolder="Select Status"
          />
        </div>

        {/* Warranty Filter */}
        <div
          className="me-2 fs-13"
          style={{ minWidth: "145px" }}
          title="Warranty Filter">
          <CommonSelect
            options={[
              { value: "", label: "Select Warranty" },
              { value: "Expired", label: "Expired" },
              { value: "Not Expired", label: "Not Expired" },
            ]}
            defaultValue={
              warrantyFilter
                ? { value: warrantyFilter, label: warrantyFilter }
                : { value: "", label: "Select Warranty" }
            }
            onChange={(selected: any) =>
              setWarrantyFilter(selected?.value || "")
            }
            placeHolder="Select Warranty"
          />
        </div>

        {/* Products Created Date Range Filter */}
        <div className="me-2">
          <div className="d-flex align-items-center">
            <PredefinedDateRanges onDateChange={handleDateChange} />
          </div>
        </div>

        {/* Clear Filters Button */}
        <div className="me-2">
          <button
            style={{ width: "120px" }}
            className="btn btn-outline-danger d-flex align-items-center position-relative justify-content-center"
            onClick={handleClearFilters}
            disabled={activeFiltersCount === 0}>
            <i className="ti ti-x me-2" />
            Clear
            {activeFiltersCount > 0 && (
              <span className="badge bg-danger rounded-pill ms-2">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProductGridFilter;
